'use client';

import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { headerLinks } from '@/components/Nav/data';
import { isNavWithChild } from './utilities';

interface IBreadcrumbsProps {
  customPath?: string;
  subPageTitle?: string;
  className?: string;
  dark?: Boolean;
  homeBreadcrumb?: string;
  contactBreadcrumb?: string;
}

const useBreadcrumbs = (
  customPath?: string,
  subPage?: string,
  homeBreadcrumb?: string,
  contactBreadcrumb?: string
) => {
  const computedPath = usePathname();

  if (homeBreadcrumb) {
    return {
      base: 'Home',
      page: homeBreadcrumb,
    };
  }

  if (contactBreadcrumb) {
    return {
      base: 'Contact',
      page: contactBreadcrumb,
    };
  }

  const path = customPath || computedPath;

  for (const base of headerLinks.filter(isNavWithChild)) {
    for (const category of base.items) {
      const current = category.items.find(({ href }) => href === path);

      if (current) {
        return {
          base: base.name,
          category: category.name,
          page: current.name,
          subPage,
        };
      }
    }
  }
};

const Breadcrumbs: React.FC<IBreadcrumbsProps> = ({
  customPath,
  subPageTitle,
  className,
  dark,
  homeBreadcrumb,
  contactBreadcrumb,
}) => {
  const breadcrumbs = useBreadcrumbs(
    customPath,
    subPageTitle,
    homeBreadcrumb,
    contactBreadcrumb
  );

  return (
    breadcrumbs && (
      <ul
        className={classNames([
          'relative mb-4 flex grow flex-wrap gap-x-3 gap-y-1 text-sm md:mb-10',
          dark ? 'text-white/60' : 'text-secondary',
          className,
        ])}
      >
        <li>{breadcrumbs.base}</li>
        <li>/</li>
        {breadcrumbs.category && (
          <>
            <li>{breadcrumbs.category}</li>
            <li>/</li>
          </>
        )}
        <li
          className={classNames([
            dark ? 'text-white' : 'text-default',
            breadcrumbs.subPage && '!text-current',
          ])}
        >
          {breadcrumbs.page}
        </li>
        {breadcrumbs.subPage && (
          <>
            <li>/</li>
            <li
              className={classNames([
                'truncate',
                dark ? 'text-white' : 'text-default',
              ])}
            >
              {breadcrumbs.subPage}
            </li>
          </>
        )}
      </ul>
    )
  );
};

export default Breadcrumbs;
